/** Keep in lockstep with AppFactory `src/pay/fee.ts`. */

export const VISIT_DEPOSIT_YUAN = 39.9;
export const PAGE_STEP = 100;

export function yuanToFen(yuan: number): number {
  return Math.round(Number(yuan || 0) * 100);
}

export function fenYuanLabel(fen?: number | null): string {
  const n = Number(fen || 0) / 100;
  return `¥${(Math.round(n * 100) / 100).toFixed(2)}`;
}

export function addonFenForPages(pageCount: number, tokenRateYuan: number): number {
  const pages = Math.max(0, Number(pageCount) || 0);
  const rateFen = yuanToFen(tokenRateYuan);
  if (!(rateFen > 0) || pages <= 0) return 0;
  return Math.round((rateFen * pages) / PAGE_STEP);
}

export function feeForActualPages(actualPages: number, tokenRateYuan: number): number {
  const pages = Math.max(0, Math.floor(Number(actualPages) || 0));
  return yuanToFen(VISIT_DEPOSIT_YUAN) + addonFenForPages(pages, tokenRateYuan);
}

export function settleAgainstQuote(params: {
  depositFen: number;
  actualPages: number;
  tokenRateYuan: number;
}) {
  const actualFeeFen = feeForActualPages(params.actualPages, params.tokenRateYuan);
  const delta = actualFeeFen - params.depositFen;
  return {
    actualFeeFen,
    supplementFen: delta > 0 ? delta : 0,
    refundFen: delta < 0 ? -delta : 0,
  };
}

export function orderIntake(order: {
  intake?: unknown;
} | null): Record<string, unknown> {
  const intake = order?.intake;
  return intake && typeof intake === 'object' && !Array.isArray(intake)
    ? intake as Record<string, unknown>
    : {};
}

export function depositFenOf(order: {
  depositFen?: number | null;
  paidFen?: number | null;
  intake?: unknown;
} | null): number {
  const intake = orderIntake(order);
  return Math.max(
    0,
    Number(order?.depositFen || intake.depositFen || intake.estimatedFeeFen || 0),
  );
}

export function tokenRateYuanOf(order: {
  intake?: unknown;
} | null): number {
  return Math.max(0, Number(orderIntake(order).tokenRateYuan || 0));
}

/** Pages the original deposit was quoted for. */
export function depositCoveredPages(order: {
  depositCoveredPages?: number | null;
  depositFen?: number | null;
  paidFen?: number | null;
  intake?: unknown;
} | null): number {
  const fromApi = Math.max(0, Math.floor(Number(order?.depositCoveredPages) || 0));
  if (fromApi > 0) return fromApi;
  const intake = orderIntake(order);
  const fromIntake = Math.max(0, Math.floor(Number(intake.pageCount) || 0));
  if (fromIntake > 0) return fromIntake;
  const depositFen = depositFenOf(order);
  const visitFen = Number(intake.visitDepositFen || 0) || yuanToFen(VISIT_DEPOSIT_YUAN);
  const rateYuan = tokenRateYuanOf(order);
  const addonFen = Math.max(0, depositFen - visitFen);
  const rateFen = yuanToFen(rateYuan);
  if (rateFen > 0 && addonFen > 0) {
    return Math.max(0, Math.round((addonFen * PAGE_STEP) / rateFen));
  }
  return 0;
}

export function processingQuotaPages(order: {
  depositCoveredPages?: number | null;
  billedPageCount?: number | null;
  depositFen?: number | null;
  paidFen?: number | null;
  intake?: unknown;
} | null): number {
  const covered = depositCoveredPages(order);
  const billed = Math.max(0, Math.floor(Number(order?.billedPageCount) || 0));
  return Math.max(covered, billed);
}
